/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, set } from 'idb-keyval';
import type { QueuedSession } from './offline-session.types';

export const OFFLINE_SESSION_QUEUE_KEY = 'corex-offline-sessions';

export interface OpenOfflineSessionInput {
  registerId: string;
  locationId: string;
  userId?: string;
  openingFloat: number;
  openedAt?: string;
}

export async function getSessionQueue(): Promise<QueuedSession[]> {
  return (await get<QueuedSession[]>(OFFLINE_SESSION_QUEUE_KEY)) ?? [];
}

export async function setSessionQueue(sessions: QueuedSession[]): Promise<void> {
  await set(OFFLINE_SESSION_QUEUE_KEY, sessions);
}

/** Opens a new offline till session, stamping a client-generated id + OPEN status. */
export async function openOfflineSession(input: OpenOfflineSessionInput): Promise<QueuedSession> {
  const session: QueuedSession = {
    clientSessionId: crypto.randomUUID(),
    registerId: input.registerId,
    locationId: input.locationId,
    userId: input.userId,
    openedAt: input.openedAt ?? new Date().toISOString(),
    openingFloat: input.openingFloat,
    status: 'OPEN',
  };
  const queue = await getSessionQueue();
  queue.push(session);
  await setSessionQueue(queue);
  return session;
}

/** Returns the currently OPEN session (there should be at most one at a time), or null. */
export async function getActiveOfflineSession(): Promise<QueuedSession | null> {
  const queue = await getSessionQueue();
  return queue.find((s) => s.status === 'OPEN') ?? null;
}

/** Closes the active (OPEN) session: sets status CLOSED, countedCash, and closedAt. */
export async function closeActiveOfflineSession(countedCash: number): Promise<QueuedSession | null> {
  const active = await getActiveOfflineSession();
  if (!active) return null;

  const patch: Partial<QueuedSession> = {
    status: 'CLOSED',
    countedCash,
    closedAt: new Date().toISOString(),
  };
  await updateSession(active.clientSessionId, patch);
  return { ...active, ...patch };
}

/** Sessions still awaiting sync: OPEN or CLOSED, but not yet SYNCED. */
export async function getPendingSessions(): Promise<QueuedSession[]> {
  return (await getSessionQueue()).filter((s) => s.status === 'OPEN' || s.status === 'CLOSED');
}

/** The local session that was CLOSED offline but hasn't synced yet (server variance
 *  unknown), or null. Distinct from `getActiveOfflineSession` (OPEN only) — this is the
 *  "closed, waiting to reconcile" limbo state between closeTill() and the next sync. */
export async function getClosedPendingSession(): Promise<QueuedSession | null> {
  const queue = await getSessionQueue();
  return queue.find((s) => s.status === 'CLOSED') ?? null;
}

/** Most recently reconciled (SYNCED) session — carries the server-computed `variance` —
 *  or null if none is awaiting acknowledgement. Call `removeSession` once the UI has
 *  shown/dismissed it so the badge doesn't reappear on every refresh. */
export async function getLastSyncedSession(): Promise<QueuedSession | null> {
  const synced = (await getSessionQueue()).filter((s) => s.status === 'SYNCED');
  if (synced.length === 0) return null;
  return synced.sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''))[0];
}

export async function updateSession(clientSessionId: string, patch: Partial<QueuedSession>): Promise<void> {
  const queue = await getSessionQueue();
  await setSessionQueue(
    queue.map((s) => (s.clientSessionId === clientSessionId ? { ...s, ...patch } : s)),
  );
}

export async function removeSession(clientSessionId: string): Promise<void> {
  const queue = await getSessionQueue();
  await setSessionQueue(queue.filter((s) => s.clientSessionId !== clientSessionId));
}
